import { getCSS, getCSSVariableValue, sumArray } from "./_helpers";
import ApexCharts, { ApexOptions } from "apexcharts";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { HttpResponse } from "@dirootie-core/http-types";
import { application } from "../../_runtime/bootstrap";
import { sum } from "@dirootie-toolbox/arrays";

type ChartData = {
  yAxis: number[];
  xAxis: string[];
};

type Props = {
  id: string;
  subTitle: string;
  seriesName: string;
  urlEndpoint: string;
  yAxisProperty: string;
  xAxisProperty: string;
  height?: string;
};

export const BarChartStatistics: FC<Props> = ({
  id,
  subTitle,
  seriesName,
  urlEndpoint,
  yAxisProperty,
  xAxisProperty,
  height = "275px",
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartReference = useMemo(() => chartRef, [chartRef]);
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState<ChartData>({
    yAxis: [],
    xAxis: [],
  });

  const refreshMode = () => {
    if (!chartReference.current) {
      return;
    }

    const height = parseInt(getCSS(chartReference.current, "height"));

    const chart = new ApexCharts(
      chartReference.current,
      getChartOptions(height, chartData, subTitle, seriesName)
    );
    if (chart) {
      chart.render();
    }

    return chart;
  };

  useEffect(() => {
    application
      .getHttpClient()
      .get(urlEndpoint)
      .then((x: HttpResponse) => {
        const data = {
          yAxis: x.body.map((x: any) => x[yAxisProperty]),
          xAxis: x.body.map((x: any) => x[xAxisProperty]),
        };

        setIsLoading(false);
        setTimeout(() => setChartData(data), 100);
      })
      .catch(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const chart = refreshMode();

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chartData]);

  return (
    <div className="px-3 pt-3">
      {!isLoading && <div id={id} ref={chartRef} style={{ height }}></div>}
    </div>
  );
};

const getChartOptions = (
  height: number,
  chartData: ChartData,
  subTitle: string,
  seriesName: string
): ApexOptions => {
  var primaryColor = getCSSVariableValue("--bs-primary");
  var secondaryColor = getCSSVariableValue("--bs-secondary");

  return {
    chart: {
      type: "bar",
      height: height,
      toolbar: {
        show: false,
      },
      fontFamily: "inherit",
      foreColor: primaryColor,
    },
    grid: {
      strokeDashArray: 4,
    },
    ...(subTitle != "" && {
      title: {
        text: sum(chartData.yAxis) + ` ${seriesName}`,
        style: {
          fontSize: "24px",
        },
      },
    }),
    ...(subTitle != "" && {
      subtitle: {
        text: subTitle,
        style: {
          fontSize: "14px",
        },
      },
    }),
    plotOptions: {
      bar: {
        columnWidth: "30%",
        borderRadiusApplication: "end",
        borderRadius: 5,
      },
    },
    xaxis: {
      categories: chartData.xAxis,
    },
    yaxis: {
      forceNiceScale: true,
    },
    dataLabels: {
      enabled: false,
    },
    series: [
      {
        name: seriesName,
        data: chartData.yAxis,
      },
    ],
    colors: [secondaryColor],
  };
};
